<template>
  <div class="box-content px-10">
    <a v-if="isEditable" @click="addBonusRule" class="absolute right-0 top-0 text-xl self-end mt-3 z-10">+ add bonus</a>
    <template v-if="isBonusEngineVisible">
      <transition-group name="bonus-rules-list" tag="div" class="box-border w-full mt-7">
        <bonus-engine-rule v-for="(bonusRule, index) in bonusRulesNotDisabled"
                           :key="bonusRule.unique_id"
                           :bonusRule.sync="bonusRulesNotDisabled[index]"
                           :bonusRulesCount="bonusRulesNotDisabled.length"
                           :programType="programType"
                           :isEditable="isEditable"
                           :eventTypes="eventTypes"
                           :eventValues="eventValues"
                           :eventValuesFilter="eventValuesFilter"
                           :filteredEventValues="filteredEventValues"
                           @increasePriority="increasePriority"
                           @decreasePriority="decreasePriority"
                           @deleteBonusRule="showDeleteBonusRuleModal"
                           @deleteBonusRuleCondition="showDeleteBonusRuleConditionModal"
                           @update:eventValuesFilter="updateEventValuesFilter"
                           class="m-auto"
        />
      </transition-group>
      <simple-modal
        v-if="bonusRuleToDelete !== null"
        class="confirm-delete-modal"
        title="delete bonus"
        resolveText="delete"
        @rejected="bonusRuleToDelete = null"
        @resolved="deleteBonusRule"
      >
        are you sure you want to delete this bonus ?
      </simple-modal>
      <simple-modal
        v-if="bonusRuleConditionToDelete !== null"
        class="confirm-delete-modal"
        title="delete element"
        resolveText="delete"
        @rejected="bonusRuleConditionToDelete = null"
        @resolved="deleteBonusRuleCondition"
      >
        are you sure you want to delete this element from the bonus engine ?
      </simple-modal>
    </template>
    <div v-else class="text-2xl text-center text-gray-400 mt-16">
      <loading-icon v-if="isLoading" />
      <span v-else>bonus engine not configured</span>
    </div>
  </div>
</template>

<script>

import _ from "lodash";
import {bonusRulesAPI} from "../../services/api/bonusRulesAPI";
import BonusEngineRule from "./BonusEngineRule";
import {SimpleModal, LoadingIcon} from "@springbig/sb-components";

export default {
  name: "BonusEngine",
  components: {
    BonusEngineRule,
    SimpleModal,
    LoadingIcon
  },
  props: {
    isEditable: Boolean,
    programType: String,
    bonusRules: Array,
    bonusRulesInit: Array,
  },
  data() {
    return {
      bonusRuleToDelete: null,
      bonusRuleConditionToDelete: null,
      eventTypes: [],
      eventValues: {},
      eventValuesFilter: {},
      eventValuesPerPage: 20,
      filteredEventValues: null,
      isLoading: true,
    }
  },
  computed: {
    bonusRulesNotDisabled() {
      const bonusRulesNotDisabled = this.bonusRules.filter(rule => !rule.disabled_at);
      return _.sortBy(bonusRulesNotDisabled, ['priority']);
    },
    isBonusEngineVisible() {
      return !!(this.bonusRulesNotDisabled.length && this.eventTypes.length && Object.values(this.eventValues).length)
    }
  },
  async created() {
    this.eventTypes = await bonusRulesAPI.getEventTypes()
    await this.loadBonusEngineEventsOptions()
    this.isLoading = false;
  },
  methods: {
    addBonusRule() {
      this.bonusRules.push({
        unique_id: _.uniqueId(),
        points: null,
        priority: this.bonusRulesNotDisabled.length ? Math.max(...this.bonusRulesNotDisabled.map(rule => rule.priority))+1 : 0,
        is_valid: false,
        disabled_at: null,
        conditions: [
          {
            unique_id: _.uniqueId(),
            condition_type: "required",
            disabled_at: null,
            event_type: null,
            event_value: null,
            event_range_min: null,
            event_range_max: null,
            is_valid: false,
          }
        ]
      });
      this.$nextTick(() => {
        const lastBonusRuleEl = document.querySelector('.bonus-rule:last-child');
        if (typeof lastBonusRuleEl.scrollIntoView !== "undefined") {
          setTimeout(() => {
            lastBonusRuleEl.scrollIntoView({block: "nearest", behavior: 'smooth'})
          }, 300)
        }
      });
    },
    loadBonusEngineEventsOptions() {
      try {
        const eventsWithOptions = this.eventTypes.filter(event => event.data_url)
        const promises = eventsWithOptions.map(async ({ data_url, key }) => {
          let options = await bonusRulesAPI.getEventOptions(data_url, { page:1, per_page:this.eventValuesPerPage })
          if (key === 'day_of_the_week') options = {data: options.data.map(day => ({...day, label:day.label.toLowerCase()})), ...options}
          return { key, options }
        })
        this.errorMessage = null
        return Promise.all(promises).then(result =>
          result.forEach(({key, options}) => this.eventValues = {...this.eventValues, [key]: options})
        )
      }
      catch(err) {
        this.errorMessage = 'error, please refresh or contact support';
      }
    },
    showDeleteBonusRuleModal(bonusRule) {
      this.bonusRuleToDelete = bonusRule
    },
    showDeleteBonusRuleConditionModal(bonusRuleCondition) {
      this.bonusRuleConditionToDelete = bonusRuleCondition
    },
    increasePriority(bonusRule) {
      const targetBonusRule = _.findLast(this.bonusRulesNotDisabled, rule => rule.priority < bonusRule.priority)
      this.swapPriorities(bonusRule, targetBonusRule)
    },
    decreasePriority(bonusRule) {
      const targetBonusRule = _.find(this.bonusRulesNotDisabled, rule => rule.priority > bonusRule.priority)
      this.swapPriorities(bonusRule, targetBonusRule)
    },
    swapPriorities(fromBonusRule, toBonusRule) {
      const currentRulePriority = fromBonusRule.priority
      fromBonusRule.priority = toBonusRule.priority
      toBonusRule.priority = currentRulePriority
    },
    async updateEventValuesFilter(filter) {
      if(!filter) {
        this.filteredEventValues = null;
        this.eventValuesFilter = {}
        return
      }
      const url = this.eventTypes.find(event => event.key === filter.eventType)?.data_url
      this.filteredEventValues = await bonusRulesAPI.getEventOptions(url, { page: filter.page+1, per_page:this.eventValuesPerPage, search:filter.searchString })
      this.eventValuesFilter = filter
    },
    deleteBonusRule() {
      // If the rule comes from the database it's disabled, otherwise it's removed
      if(this.bonusRuleToDelete.id !== undefined) {
        const bonusRuleInit = this.bonusRulesInit.find(bonusRule => bonusRule.id === this.bonusRuleToDelete.id)
        this.bonusRuleToDelete.conditions = bonusRuleInit.conditions.map( c => ({...c, disabled_at: new Date()}) )
        this.bonusRuleToDelete.disabled_at = new Date()
        this.bonusRuleToDelete.is_valid = true
      }else{
        this.$emit('update:bonusRules', this.bonusRules.filter( rule => this.bonusRuleToDelete.unique_id !== rule.unique_id ))
      }
      this.bonusRuleToDelete = null
      // Reordering priorities
      for(let [key, bonusRule] of this.bonusRulesNotDisabled.entries()) {
        bonusRule.priority = key
      }
    },
    deleteBonusRuleCondition() {
      // If the condition comes from the database it's disabled, otherwise it's removed
      if(this.bonusRuleConditionToDelete.id !== undefined) {
        const bonusRuleConditionInit = this.bonusRulesInit
         .reduce((conditions, bonusRule) => [...conditions, ...bonusRule.conditions], [])
         .find(condition => condition.id === this.bonusRuleConditionToDelete.id)

        const conditionToDelete = {
          event_type: bonusRuleConditionInit.event_type,
          event_value: bonusRuleConditionInit.event_value,
          event_range_min: bonusRuleConditionInit.event_range_min,
          event_range_max: bonusRuleConditionInit.event_range_max,
          disabled_at: new Date(),
          is_valid: true,
        }

        this.$emit(
          'update:bonusRules',
          this.bonusRules.map(
            rule => ({...rule, conditions:rule.conditions.map(
                condition => this.bonusRuleConditionToDelete.id === condition.id
                  ? {...condition, ...conditionToDelete}
                  : condition
              )})
          )
        )
      }else{
        this.$emit(
          'update:bonusRules',
          this.bonusRules.map(
            rule => ({...rule, conditions:rule.conditions.filter(
              condition => this.bonusRuleConditionToDelete.unique_id !== condition.unique_id
            )})
          )
        )
      }
      this.bonusRuleConditionToDelete = null
    }
  },
  watch: {
    bonusRules: {
      deep: true,
      handler(rules) {
        this.$emit('errorChanged', rules.some(rule => rule.is_valid !== true))
      },
    }
  }
}
</script>

<style lang="scss" scoped>
.confirm-delete-modal::v-deep {
  header {
    font-family: AileronLight,sans-serif;
  }
  .content {
    font-size: 2rem;
    text-align: center;
    padding: 0 30px;
    max-width: 425px;
    min-width: auto;
  }
  footer {
    flex-direction: column;
    align-items: center;
    >.sb-button {
      width: 180px;
      padding: 0.5rem 3rem;
    }
    >.sb-reject {
      color: #128ff9;
      background-color: white;
    }
  }
  .close {
    font-size: initial;
    opacity: 0.5
  }
}

.bonus-rules-list-move {
  transition: transform .3s;
}
.bonus-rules-list-enter-active {
  transition: all .3s;
}
.bonus-rules-list-enter {
  opacity: 0;
  transform: translateY(-30px);
}
</style>
